import {
  Box,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SpaceProps,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  TypographyProps,
} from '@chakra-ui/react';
import React from 'react';

import {
  finleyAssertUnreachable,
  utils as commonUtils,
} from '@finley-technologies/common';
import { DataPipeline } from '@finley-technologies/finley-types';

import { colors } from '~/packages/common-frontend/colors';

// Constants
const spaceBetween = '20px';

// Fragments
type BadgedTextProps = TypographyProps &
  SpaceProps & { children: React.ReactNode };
const BadgedText = ({
  children,
  fontSize,
  fontWeight,
  padding,
}: BadgedTextProps): React.ReactElement => (
  <Text
    display="inline-block"
    bg={colors.gray[200]}
    fontSize={fontSize ?? 'sm'}
    fontWeight={fontWeight ?? 'semibold'}
    mt="5px"
    mb="5px"
    padding={padding ?? '5px 8px'}
    borderRadius="8px"
    overflowWrap="anywhere"
  >
    {children}
  </Text>
);

const SmallBadgedText = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => (
  <BadgedText fontSize="xs" fontWeight="medium" padding="3px 8px">
    {children}
  </BadgedText>
);

// Field names can be very long, so set a maxWidth + overflowWrap=anywhere
const FieldNameCell = ({
  column: { name, type, required },
}: {
  column: DataPipeline.DataDictionaryColumn;
}): React.ReactElement => (
  <Td maxWidth="40%">
    <Text>
      <BadgedText>{name}</BadgedText>
    </Text>
    <Text fontSize="sm">Type: {type}</Text>
    {!required && (
      <Text>
        <SmallBadgedText>Optional</SmallBadgedText>
      </Text>
    )}
  </Td>
);

const DescriptionCell = ({
  column: { description, defaultValue },
}: {
  column: DataPipeline.DataDictionaryColumn;
}): React.ReactElement => (
  <Td>
    <Text>{description}</Text>
    {defaultValue !== null && (
      <Text mt="20px">
        Default if unspecified:{' '}
        <SmallBadgedText>{defaultValue}</SmallBadgedText>
      </Text>
    )}
  </Td>
);

const ExamplesCell = ({
  column: { examples },
}: {
  column: DataPipeline.DataDictionaryColumn;
}): React.ReactElement => (
  <Td>
    {examples.map((example) => (
      <Box key={example}>
        <SmallBadgedText>{example}</SmallBadgedText>
      </Box>
    ))}
  </Td>
);

const SheetName = ({
  sheetName,
  isSheetNameRegex,
}: {
  sheetName: string;
  isSheetNameRegex: boolean;
}): React.ReactElement => (
  <>{isSheetNameRegex ? `matching /${sheetName}/` : sheetName}</>
);

// columns of a tape, or of a table on a sheet.
const ColumnsTable = ({
  columns,
}: {
  columns: DataPipeline.DataDictionaryColumn[];
}): React.ReactElement => (
  <Table mb={spaceBetween}>
    <Thead>
      <Tr>
        <Th>Field Name</Th>
        <Th>Description</Th>
        <Th>Sample Data</Th>
      </Tr>
    </Thead>
    <Tbody>
      {columns.map((column) => (
        <Tr key={column.name}>
          <FieldNameCell column={column} />
          <DescriptionCell column={column} />
          <ExamplesCell column={column} />
        </Tr>
      ))}
    </Tbody>
  </Table>
);

const ValuesTable = ({
  columns,
}: {
  columns: Extract<
    DataPipeline.DataDictionaryEntry,
    { kind: 'xlsxValues' }
  >['columns'];
}): React.ReactElement => (
  <Table mb={spaceBetween}>
    <Thead>
      <Tr>
        <Th>Location</Th>
        <Th>Field Name</Th>
        <Th>Description</Th>
        <Th>Sample Data</Th>
      </Tr>
    </Thead>
    <Tbody>
      {columns.map((column) => (
        <Tr key={column.name}>
          <Td>
            <Text>
              <SheetName
                sheetName={column.sheetName}
                isSheetNameRegex={column.isSheetNameRegex}
              />
            </Text>
            <Text>{column.cellRange}</Text>
          </Td>
          <FieldNameCell column={column} />
          <DescriptionCell column={column} />
          <ExamplesCell column={column} />
        </Tr>
      ))}
    </Tbody>
  </Table>
);

const EntryHeading = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => (
  <Heading fontSize="14" mb={spaceBetween}>
    {children}
  </Heading>
);

const DataDictionaryEntry = ({
  entry,
  showTapeHeading,
}: {
  entry: DataPipeline.DataDictionaryEntry;
  showTapeHeading: boolean;
}): React.ReactElement => {
  const title = commonUtils.toTitleCase(entry.artifactSlug);
  switch (entry.kind) {
    case 'tape':
      return (
        <Box>
          {showTapeHeading && <EntryHeading>{title}</EntryHeading>}
          <ColumnsTable columns={entry.columns} />
        </Box>
      );
    case 'xlsxRange':
      return (
        <Box>
          <EntryHeading>
            {title} (Sheet:{' '}
            <SheetName
              sheetName={entry.sheetName}
              isSheetNameRegex={entry.isSheetNameRegex}
            />
            , Header range: {entry.headerRange})
          </EntryHeading>
          <ColumnsTable columns={entry.columns} />
        </Box>
      );
    case 'xlsxValues':
      return (
        <Box>
          <EntryHeading>{title}</EntryHeading>
          <ValuesTable columns={entry.columns} />
        </Box>
      );
    default:
      return finleyAssertUnreachable(entry);
  }
};

// what an uploaded file must contain (`MeshInputFile.dataDictionary`).
export const DataDictionaryTables = ({
  dataDictionary,
}: {
  dataDictionary: DataPipeline.DataDictionaryEntry[];
}): React.ReactElement => (
  <>
    {dataDictionary.map((entry) => (
      <DataDictionaryEntry
        key={entry.artifactSlug}
        entry={entry}
        // a lone tape needs no heading: it is the whole file.
        showTapeHeading={dataDictionary.length > 1}
      />
    ))}
  </>
);

export const DataDictionaryModal = ({
  isOpen,
  onClose,
  dataDictionary,
}: {
  isOpen: boolean;
  onClose: () => void;
  dataDictionary: DataPipeline.DataDictionaryEntry[];
}): React.ReactElement => (
  <Modal isOpen={isOpen} onClose={onClose} size="lg">
    <ModalOverlay />
    <ModalContent padding="20px" width="auto" minW="5xl">
      <ModalCloseButton />
      <ModalBody>
        <Heading fontSize="16" mb={spaceBetween}>
          Data Tape Format
        </Heading>
        <DataDictionaryTables dataDictionary={dataDictionary} />
      </ModalBody>
    </ModalContent>
  </Modal>
);
